// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { IStrings } from '../../../IStrings';

export const strings: IStrings = {
  emptyList: 'There are no GMM managed groups',
  loading: 'Loading',
  refresh: 'Refresh',
  continue: 'Continue',
  addOwner204Message: 'Added Successfully.',
  addOwner400Message: 'GMM is already added as an owner.',
  addOwner403Message: 'You do not have permission to complete this operation.',
  addOwnerErrorMessage:
    'We are having trouble adding GMM as the owner. Please try again later.',
  okButton: 'OK',
  groupIdHeader: 'Enter Group ID',
  groupIdPlaceHolder: 'Group ID',
  addOwnerButton: 'Add GMM as an owner',
  membershipManagement: 'Membership Management',
  learnMembershipManagement:'Learn how Membership Management works in your organization',
  maintenanceTitle: 'This application is currently under maintenance.',
  maintenanceMessage: 'The GMM UI is under maintenance. Please try again later. Thank you for your patience.',
  permissionDenied: 'You do not have permission to view this page. Please contact your administrator.',
  HROnboarding: {
    orgLeader: "Org. leader",
    provideOrgLeader: "Provide Org. leader",
    orgLeaderPlaceHolder: "Please enter Org. leader",
    orgLeaderInfo: "Provide Org. leader",
    depth: "Depth",
    depthPlaceHolder: "Please enter Depth",
    depthInfo: "Defines the maximum depth level in the organization hierarchy used to retrieve data. The default value is 'All' which means there is no depth limit and includes everyone under this org leader. If depth is '1 level down' is specified, the org leader and everyone directly reporting to the org leader will be retrieved.",
    incrementButtonAriaLabel: "Increase value by 1",
    decrementButtonAriaLabel: "Decrease value by 1",
    filter: "Filter",
    filterPlaceHolder: "Please enter Filter",
    filterInfo: "Provide filter",
    includeOrg: "Do you want this source to be based on organization structure?",
    includeFilter: "Do you want to filter by {0} attributes?",
    includeLeader: "Include leader",
    group: "Group",
    ungroup: "Ungroup",
    attributeTitle: "Attributes",
    addAttribute: "Add attribute",
    attribute: "Attribute",
    equalityOperator: "Equality operator",
    attributeValue: "Value",
    orAndOperator: "And/Or",
    attributeInfo: "Select the attribute",
    equalityOperatorInfo: "Select the equality operator",
    equalityOperatorInfoTitle: 'How to use the operators:',
    inOperatorDescription: '{0} Operator — "Is it one of these?"{1} The {0} operator checks if a value matches any value in a list.{1}→ Example: Get users who live in Seattle, Portland, or San Francisco. → City {0} (Seattle, Portland, San Francisco) will return all users who live in any of these cities.',
    notInOperatorDescription: '{0} Operator — "Is it not one of these?"{1} The {0} operator checks if a value does not match any value in a list.{1}→ Example: Get users who do not live in Seattle, Portland, or San Francisco. → City {0} (Seattle, Portland, San Francisco) will return all users who do not live in any of these cities.',
    notEqualToOperatorDescription: '{0} Operator — "Is it different?"{1} The {0} operator means "not equal to". It checks if a value is different from another.{1}→ Example: Get users who do not live in Seattle. → City {0} Seattle will return all users who do not live in Seattle.',
    attributeValueInfo: "Select the attribute value",
    orAndOperatorInfo: "Select And/Or",
    missingAttributeErrorMessage: "Please provide missing info in the previous attribute",
    customOrgLeaderMissingErrorMessage: " doesn't exist in the ",
    source: " source",
    orgLeaderMissingErrorMessage: " doesn't exist in the HR Data source",
    invalidInputErrorMessage: "Invalid input. Please enter only numbers.",
    valueComboBoxOptionCodeLabel: "Code: ",
    descriptionLabel: "Description: ",
    attributeDisabledErrorMessage: 'This attribute has been disabled. If you remove it and later want to add it back, you will need to reach out to {email} for assistance.',
    supportPlaceHolder: "Admin",
    all: "All",
    level: "level",
    levelsPlural: "s",
    down: "down",
    up: "up",
    andOrInfoTitle: "How to use AND / OR:",
    andLogicDescription: "{0} means people must match {1} to be included.{2}→ Example: Department is A {3} Location is X – only people who are in Department A and located in X will be included.",
    orLogicDescription: "{0} means people who match {1} one of the conditions will be included.{2}→ Example: Department is A {3} Location is X – people who are in Department A or located in X will be included.",
    AND: "AND",
    OR: "OR",
    any: "any",
    allLowercase: "all",
    IN: "IN",
    NOTIN: "NOT IN",
    notEqualTo: "<>",
    loadingText: "Loading...",
  },
  Components: {
    AppHeader: {
      title: 'Group Membership Management',
      settings: 'Settings',
    },
    Banner: {
      bannerMessageStart: "Need help? ",
      clickHere: "Click here",
      bannerMessageEnd: " to learn more about how Membership Management works in your organization.",
      expandBanner: "Expand banner",
    },
    HyperlinkSetting: {
      address: "Address",
      addHyperlink: "Add hyperlink",
      invalidUrl: "Invalid URL",
    },
    GroupQuerySource: {
      searchGroupSuggestedText: "Suggested Groups",
      noResultsFoundText: "No results found",
      loadingText: "Loading",
      selectionAriaLabel: "Selected contacts",
      removeButtonAriaLabel: "Remove",
      validQuery: "Query is valid.",
      invalidQuery: "Failed to parse query. Ensure it is valid JSON.",
      searchGroupName: "Search group name",
    }
  },
  AdminConfig: {
    labels: {
      pageTitle: "Admin Center",
      saveButton: "Save",
      saveSuccess: "Saved successfully.",
    },
    Errors:{
      forbidden: 'Forbidden'
    },
    HyperlinkSettings: {
      labels: {
        hyperlinks: "Hyperlinks",
        description: "Provide hyperlinks to the following organization specific information so that your users are empowered to leverage XMM to its fullest.",
      },
      dashboardLink: {
        title: "Dashboard",
        description: "This is the link that shows on the top right corner of the dashboard. It takes you to an internal site that has all the details on how to leverage XMM at your organization. This could include FAQs, contact information, SLAs, etc.",
      },
      outlookWarningLink: {
        title: "Destination Instructions",
        description: "This link appears when Outlook is involved in the group selected. It takes you to an internal site to make the proper settings when sending emails to the Outlook group.",
      },
      privacyPolicyLink: {
        title: "Privacy Policy",
        description: "This is the link that shows on the bottom left corner of the dashboard. It takes you to an internal site that has all the details on how XMM handles and stores user data.",
      },
    },
    Operations: {
      labels: {
        operations: "Operations",
        title: "GMM Control Panel",
        description: "Use the stop button to halt GMM operations and the reset button to reboot GMM at any time.",
      },
      buttons: {
        stop: "Stop GMM",
        stopping: "Stopping",
        reset: "Reset GMM",
        resetting: "Resetting",
        start: "Start GMM",
        starting: "Starting",
      }
    },
    CustomSourceSettings: {
      labels: {
        customSource: "Custom Source",
        sourceDescription: "Assign a friendly name to the source of your organization's data so that group owners recognize it when defining the source of their membership destinations.",
        sourceCustomLabelInput: "Custom Label",
        listOfAttributes: "List of Attributes",
        listOfAttributesDescription: "Assign a friendly name to each of the attributes in your custom source so that group owners recognize them when defining the source of their membership destinations.",
        attributeColumn: "Attribute",
        customLabelColumn: "Custom Label",
        customLabelInputPlaceHolder: "Enter a custom label",
        valuesColumn: "Values",
        valuesDropdownSpinnerLabel: "Loading values...",
        valuesDropdownPlaceholder: "View values",
        valuesDropdownTitle: "Select a value",
        descriptionColumn: "Description",
        descriptionPlaceHolder: "Enter a custom description",
        enabledColumn: "Enabled",
        enabledToggleTitle: "Toggle to enable/disable attribute",
      },
    },
    GeneralSettings: {
      labels: {
        general: "General",
        reviewOwnSubmissionTitle: "Submission Reviewers permissions",
        reviewOwnSubmissionDescription: "Can submission reviewers review their own submissions?",
        createGroupTitle: "Create Group feature enabled",
        createGroupDescription: "Allow users to create groups in your organization.",
        businessJustificationTitle: "Business Justification required",
        businessJustificationDescription: "Require users to provide business justification when requesting to manage a group or perform an update?",
        isDisclaimerEnabledTitle: "Is the initial visit disclaimer enabled?",
        isDisclaimerEnabledDescription: "Enable the disclaimer to be shown to users when they first visit GMM.",
      }
    }
  },
  Authentication: {
    loginFailed: 'An unexpected error occurred during login.'
  },
  JobDetails: {
    labels: {
      pageTitle: 'Membership Details',
      sectionTitle: 'Membership Details',
      lastModifiedby: 'Last Modified by',
      requestedBy: 'Requested by',
      requestedOnBehalfOf: 'Requested on behalf of',
      groupLinks: 'Group Links',
      destination: 'Destination',
      type: 'Type',
      name: 'Name',
      ID: 'ID',
      teamName: 'Team Name',
      channelName: 'Channel Name',
      channelId: 'Channel Id',
      configuration: 'Configuration',
      startDate: 'Start Date',
      endDate: 'End Date',
      lastRun: 'Last Run',
      nextRun: 'Next Run',
      frequency: 'Frequency',
      frequencyDescription: 'Every {0} hrs',
      requestor: 'Requestor',
      increaseThreshold: 'Increase Threshold',
      decreaseThreshold: 'Decrease Threshold',
      noThresholdSet: 'No threshold configured',
      thresholdViolations: 'Threshold Violations',
      sourceParts: 'Source Parts',
      membershipStatus: 'Membership status',
      sync: 'Sync',
      enabled: 'Enabled',
      disabled: 'Disabled',
      pendingReview: 'Pending Review',
      pendingReviewDescription: 'Your submission is awaiting to be reviewed.',
      pendingReviewInstructions: 'Please review this request and then approve or decline after reviewing the membership configuration.',
      businessJustification: 'Business Justification',
      approve: 'Approve',
      reject: 'Reject',
      submissionRejected: 'Submission Rejected',
      rejectionDialogTitle: 'Reject Submission',
      rejectionDialogSubText: 'Please provide a reason for rejecting this submission.',
      rejectionReasonLabel: 'Rejection Reason',
      rejectionReasonPlaceholder: 'Enter the reason for rejecting this submission...',
      submitRejection: 'Submit',
      submittingRejection: 'Submitting...',
      removeGMM: 'Remove GMM Management',
      removeGMMWarning: 'Are you sure you want to remove GMM management from this group? You will need to manually remove GMM as a group owner in Microsoft Entra ID.',
      removeGMMConfirmation: 'Confirm (Link opens in new tab)',
    },
    descriptions: {
      requestedBy: 'User who made the change to this job.',
      requestedOnBehalfOf: 'User on whose behalf the change was made to this job.',
      startDate: 'Date of the onboarding of this job into GMM.',
      endDate: 'Date of the last run of this job.',
      type: 'The destination\'s type. Currently GMM only manages the membership of Group destinations. In the future it will also support Teams Channel membership.',
      id: 'Object ID of the destination group.',
      lastRun: 'Time of the last run of this job.',
      nextRun: 'Next run time for this job.',
      frequency: 'How often this job runs.',
      requestor: 'User who requested this job to be onboarded into GMM.',
      increaseThreshold:
        'If GMM detects large changes in membership, you (the group owner) may want to review these changes.  This setting allows GMM to suspend updates if a threshold you define is exceeded. This value (expressed as a percentage of the current size of the group) limits the number of users that can be added to the target group.  If the number is exceeded, GMM will wait for your approval to continue.',
      decreaseThreshold:
        'If GMM detects large changes in membership, you (the group owner) may want to review these changes.  This setting allows GMM to suspend updates if a threshold you define is exceeded. This value (expressed as a percentage of the current size of the group) limits the number of users that can be removed from the target group.  If the number is exceeded, GMM will wait for your approval to continue.',
      thresholdViolations: 'Number of times a threshold was exceeded.',
    },
    MessageBar: {
      dismissButtonAriaLabel: 'Close',
    },
    Errors:{
      jobInProgress: 'Job is in progress. Please try again later.',
      forbidden: 'This action is forbidden.',
      internalError: 'We can\'t process your request at this time. Please try again later',
      removeGMMError: 'Error removing GMM management from this group:',
      submitterNotOwner: 'This submission was rejected because the requestor was no longer an owner when the submission was reviewed.',
      reviewerCannotReviewOwnSubmission: 'This cannot be completed because the reviewer cannot review their own submission.',
      rejectionError: 'Failed to reject submission. Please try again.',
    },
    Panel: {
      dismissButtonAriaLabel: 'Dismiss job history panel',
      changeTimeColumnLabel: 'Date',
      changedByColumnLabel: 'Modified By',
      changeReasonColumnLabel: 'Change Type',
      changeDetailsColumnLabel: 'Details',
      viewDetails: 'View Details',
      history: 'History',
      configurationPivotHeader: 'Configuration',
      onboardingRequest: 'Onboarding Request',
      statusUpdate: 'Status Update',
      update: 'Sync Update Request',
      submissionApproved: 'Submission Approved',
      submissionRejected: 'Submission Rejected',
      businessJustification: 'Business Justification',
      groupSettings: 'Group Settings'
    },
    openInAzure: 'Open in Azure',
    openInTeams: 'Open in Teams',
    viewDetails: 'View Details',
    editButton: 'Edit',
    notFound: 'Membership syncs for group with object Id {0} are now disabled because the group no longer exists.',
  },
  JobsList: {
    listOfMemberships: 'Managed groups',
    ShimmeredDetailsList: {
      toggleSelection: 'Toggle selection',
      toggleAllSelection: 'Toggle selection for all items',
      selectRow: 'select row',
      ariaLabelForShimmer: 'Content is being fetched',
      ariaLabelForGrid: 'Item details',
      columnNames: {
        name: 'Name',
        type: 'Type',
        email: 'Email',
        lastRun: 'Last Run',
        nextRun: 'Next Run',
        status: 'Status',
        actionRequired: 'Action Required',
      },
    },
    MessageBar: {
      dismissButtonAriaLabel: 'Close',
    },
    PagingBar: {
      previousPage: 'Prev',
      nextPage: 'Next',
      page: 'Page',
      of: 'of',
      display: 'Display',
      items: 'Items per page',
      pageNumberAriaLabel: 'Page number',
      pageSizeAriaLabel: 'Items per page',
    },
    JobsListFilter: {
      filters: {
        ID: {
          label: 'ID',
          placeholder: 'Search',
          validationErrorMessage: 'Invalid GUID!',
        },
        status: {
          label: 'Status',
          options: {
            all: 'All',
            enabled: 'Enabled',
            disabled: 'Disabled',
          },
        },
        actionRequired: {
          label: 'Action Required',
          options: {
            all: 'All',
            thresholdExceeded: 'Threshold Exceeded',
            customerPaused: 'Paused',
            developerPaused: 'Developer Paused',
            membershipDataNotFound: 'No users in the source',
            destinationGroupNotFound: 'Destination Group Not Found',
            notOwnerOfDestinationGroup: 'Not Owner Of Destination Group',
            securityGroupNotFound: 'Security Group Not Found',
            pendingReview: 'Pending Review',
            submissionRejected: 'Submission Rejected',
          },
        },
        destinationType: {
          label: 'Destination Type',
          options: {
            all: 'All',
            channel: 'Channel',
            group: 'Group'
          }
        },
        destinationName: {
          label: 'Destination',
          placeholder: 'Search by Name, Email, or Object ID',
        },
        ownerPeoplePicker: {
          label: 'Owner',
          suggestionsHeaderText: 'Suggested People',
          noResultsFoundText: 'No results found',
          loadingText: 'Loading',
          selectionAriaLabel: 'Selected contacts',
          removeButtonAriaLabel: 'Remove'
        },
      },
      filterButtonText: 'Filter',
      clearButtonTooltip: 'Clear Filters',
    },
    NoResults: 'No memberships found.',
  },
  ManageMembership: {
    manageMembershipButton: 'Manage Membership',
    addSyncButton: 'Add Sync',
    bulkAddSyncsButton: 'Bulk Add Syncs',
    bulkApproveSyncsButton: 'Bulk Approve Syncs',
    downloadButton: 'Download',
    downloadingButton: 'Downloading',
    uploadHeader: 'Upload CSV File',
    uploadingLabel: 'Uploading…',
    chooseFileButton: 'Choose File',
    selectCSVFileLabel: 'Select CSV File',
    csvErrorMessage: 'An unknown error occurred while parsing the CSV file. Please try again later.',
    uploadErrorMessage: 'Please upload a valid CSV file.',
    uploadCompleteLabel: 'Upload complete:',
    totalNumberOfJobsLabel: 'Total # of jobs:',
    totalNumberOfApprovedJobsLabel: '# of approved jobs:',
    approveErrorStatusLabel: 'Error',
    approveButton: 'Approve',
    labels: {
      abandonOnboarding: 'Abandon Onboarding?',
      abandonOnboardingDescription: 'Are you sure you want to abandon the in-progress onboarding and go back?',
      alreadyOnboardedWarning: 'This group is already onboarded.',
      confirmAbandon: 'Yes, go back',
      pageTitle: 'Manage Membership',
      step1title: 'Step 1: Select Destination',
      step1description: 'Please select the destination type and the destination whose membership you want to manage.',
      selectOrCreateGroup: 'Select or create a group',
      selectDestinationType: 'Select Destination Type',
      selectDestinationTypePlaceholder: 'Select an option',
      selectDestination: 'Select existing destination',
      createNewGroup: 'Create a new group',
      searchGroup: 'Search group',
      searchTeam: 'Search team',
      searchChannel: 'Search channel',
      searchGroupSuggestedText: 'Suggested Groups',
      searchChannelSuggestedText: 'Suggested Channels',
      noResultsFound: 'No results found',
      appsUsed: 'This group uses the following apps:',
      entraSecurityGroup: 'This is an Entra Security Group',
      outlookWarning: 'There are important settings that should be considered before sending an email to this Outlook group. Please follow the instructions provided by your organization.',
      teamsNotSupportedWarning: 'Warning: This group is not Teams-enabled so Teams channel cannot be managed by GMM.',
      appIdNotOwnerWarning: 'To manage this group\'s membership GMM must be an owner of the group.{2} {0}{2}Once you have done that, come back and click:{2}{1}',
      addOwnerMessage: 'Add {0} as an owner.',
      teamsServiceAccountNotOwnerWarning: "GMM is not the owner of this Teams Channel! It will not be able to manage membership for this channel until you add it.{2} {0}{2}Once you have done that, come back and click:{2}{1}",
      userNotOwnerWarning: 'Warning: You are not the owner of this group! You can only manage memberships with GMM for groups you own.',
      groupDescription: 'Groups can be Outlook / M365 Group, Entra Security Group, Teams, Viva Engage community, and SharePoint site etc.',
      channelDescription: 'Channel can be Teams private or shared channel.',
      step2title: 'Step 2: Run Configuration',
      step2description: '',
      advancedQuery: 'Advanced Query',
      advancedView: 'Advanced View',
      query: 'Query',
      validQuery: 'Query is valid.',
      invalidQuery: 'Failed to parse query. Ensure it is valid JSON.',
      invalidGroups: 'Invalid group IDs:',
      invalidSqlFilters: "Invalid Sql filter. Ensure each filter is valid Sql.",
      step3title: 'Step 3: Membership Configuration',
      step3description: 'Specify the source membership for the destination group. You can add multiple source parts, each with its own criteria. GMM will merge all source parts to determine the membership for the destination group.',
      selectStartDate: 'Select an option to start managing the membership',
      ASAP: 'ASAP– After approval, the first sync will run as soon as possible',
      requestedDate: 'Requested Date– Once approved, the first sync will proceed on the Requested Date or ASAP if the requested date has already passed.',
      selectRequestedStartDate: 'Select a requested start date',
      from: 'From',
      frequency: 'The Membership Management solution defaults to syncing every 24 hours. Please contact your admin to request a different frequency.',
      hrs: 'hrs',
      preventAutomaticSync: 'Prevent automatic synchronization if membership change exceeds increase and/or decrease threshold?',
      preventAutomaticSyncInfo: 'Selecting \'Yes\' means extra protection and visibility. If a drastic membership change is detected, group owners will receive a detailed alert notification to approve or pause the synchronization attempts. For example, with a 20% increase threshold and a 15% decrease threshold on 100 members, synchronization will pause above 120 members or below 85 members.',
      preventAutomaticSyncWarning: 'Warning: Selecting \'No\' means membership synchronizations will occur without notification, regardless of how drastic of an increase or decrease in membership GMM projects.  This can be painful in situations where a misconfigured change in membership definition (ex: by a group owner) or an errant change in the source information (ex: by someone in HR) occurs.',
      increase: 'Increase',
      decrease: 'Decrease',
      step4title: 'Step 4: Confirmation',
      step4description: '',
      objectId: 'Object ID',
      sourceParts: 'Source Parts',
      sourcePart: "Source Part",
      noThresholdSet: 'No threshold set',
      savingSyncJob: 'Saving...',
      updatingSyncJob: 'Updating...',
      group: 'Group',
      destinationPickerSuggestionsHeaderText: 'Suggested destinations',
      expand: 'Expand',
      collapse: 'Collapse',
      sourceType: 'Source Type',
      addSourcePart: 'Add Source Part',
      excludeSourcePart: 'Exclude Source Part',
      deleteLastSourcePartWarning: 'Cannot delete the last source part.',
      errorOnSchema: 'Expected {0} but got type {1} at {2}.',
      searchGroupName: 'Search group name',
      HR: 'HR',
      groupMembership: 'Group Membership',
      groupOwnership: 'Group Ownership',
      placeMembership: 'Place Membership',
      clickHere: 'Click here',
      requestor: 'Requestor',
      requestorInfo: 'The requestor is the person who initially requested that GMM manage this destination\'s membership. This person must be an owner of the destination. This helps us track who to reach out to if we have questions about the initial onboarding. Otherwise, the requestor has no additional rights to the destination than any other owner.',
      businessJustificationSubtitle: 'What is the purpose of this destination?',
      businessJustificationPrompt: 'Please provide a specific business justification. This will help the reviewer evaluate your request and offer feedback, if necessary.',
      businessJustificationPlaceholder: 'Enter your answer',
      requestedBy: 'Requested by',
      requestedOnBehalfOf: 'Requested on behalf of',
      requestedOnBehalfOfPlaceholder: 'Select group owner',
      requestedOnBehalfOfDescription: 'This is the group owner of the destination on whose behalf you are requesting this job.',
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
    },
    CreateGroup: {
      createNewGroup: 'Create New Group',
      groupName: 'Group Name',
      groupNamePlaceholder: 'Enter the name of the group',
      groupAlias: 'Group alias',
      groupAliasPlaceholder: 'Enter the alias of the group',
      creating: 'Creating...',
      created: 'Created',
      createGroup: 'Create group',
      authorizedSenders: 'Authorized Senders',
      authorizedSendersToolTip: 'Specify individuals or groups who are permitted to send messages. If a group is specified, all members of that group will have send permissions. Leave blank to allow anyone to send. This field is required for groups with more than 5,000 members.',
      hiddenFromExchangeClients: 'Hidden from Exchange clients',
      hiddenFromExchangeClientsToolTip: 'When hidden, this group will not appear in the Groups section of the Outlook client. Setting this to true also removes member access to the group mailbox, including historical messages and calendar invites.',
      welcomeMessageEnabled: 'Welcome message enabled',
      welcomeMessageEnabledToolTip: 'Controls whether a welcome message is delivered in email when new members are added to the group.'
    }
  },
  Disclaimer: {
    title: 'Please read and agree to the following before proceeding:',
    submitButton: 'I Agree',
    membershipRules: 'I will double-check membership rules before submission and acknowledge that members will be populated based on the specified criteria. {0}',
    membershipRulesBoldNote: 'I accept responsibility for any errors caused by incorrect criteria.',
    outlookWelcomeMessage: 'I acknowledge that GMM will disable welcome message for Outlook Groups.',
    autoSubscribeSettings: 'I acknowledge that both {0} and {1} must be {2} to enable sending email notifications and calendar invites directly to members of the group. GMM will apply these settings to your group, but they only take effect for users added after configuration. {3}',
    autoSubscribeSettingsBoldNote: 'To ensure every member is subscribed, onboard an empty group to GMM so that all users are added—and correctly configured—through GMM.',
    autoSubscribeNewMembersLabel: 'AutoSubscribeNewMembers',
    autoSubscribeNewMembersDescription: 'The AutoSubscribeNewMembers switch specifies whether to automatically subscribe new members that are added to the Microsoft 365 Group to conversations and calendar events. Only users that are added to the group after you enable this setting are automatically subscribed to the group.',
    autoSubscribeNewMembersDescriptionLinkText: 'Read more',
    autoSubscribeNewMembersDescriptionLinkUrl: 'https://learn.microsoft.com/en-us/powershell/module/exchange/set-unifiedgroup?view=exchange-ps#-autosubscribenewmembers',
    subscribeMembersToCalendarEventsLabel: 'SubscribeMembersToCalendarEvents',
    subscribeMembersToCalendarEventsDescription: 'The AlwaysSubscribeMembersToCalendarEvents switch controls the default subscription settings of new members that are added to the Microsoft 365 Group. Changing this setting doesn\'t affect existing group members.',
    subscribeMembersToCalendarEventsDescriptionLinkText: 'Read more',
    subscribeMembersToCalendarEventsDescriptionLinkUrl: 'https://learn.microsoft.com/en-us/powershell/module/exchange/set-unifiedgroup?view=exchange-ps#-alwayssubscribememberstocalendarevents',
    authorizedSenders: 'I acknowledge that for {0} to prevent email delivery failures. For groups with fewer than 5,000 members, this is at the group owner\'s discretion.',
    authorizedSendersBoldNote: 'Outlook Groups with more than 5,000 members authorized senders must be set',
    teamsVivaNotifications: 'I acknowledge that for Teams and Viva Engage members will be notified according to their platform\'s notification flow, which cannot be bypassed.',
    flatList: 'I acknowledge that M365 Groups do not support nesting and all members are included in a flat list.',
  },
  copy: 'Copy',
  remove: 'Remove',
  delete: 'Delete',
  edit: 'Edit',
  submit: 'Submit',
  needHelp: 'Need help?',
  next: 'Next',
  close: 'Close',
  cancel: 'Cancel',
  learnMore: 'Learn more',
  errorItemNotFound: 'Item not found',
  welcome: 'Welcome',
  back: 'Back',
  backToDashboard: 'Back to dashboard',
  version: 'Version',
  yes: 'Yes',
  no: 'No',
  or: 'Or',
  and: 'And',
  true: 'True',
  false: 'False',
  privacyPolicy: 'Privacy Policy',
  hoursAgo: '{0}, {1} hrs ago',
  hoursLeft: '{0}, {1} hrs left',
  pendingInitialSync: 'Pending initial sync',
};
